/**
 * SiteHeader — client component
 *
 * Must be a client component because @nself-web/ui bundles hooks (useState,
 * useRef, useCallback, useEffect) into its dist without a "use client"
 * directive. Marking this wrapper as client satisfies RSC requirements until
 * the ui package adds the directive to its dist bundle.
 *
 * Renders the @nself-web/ui <Header /> as the top chrome for sentry.nself.org.
 *
 * Follows the ɳTask SaaS header pattern (web/ntask/src/layouts/AppShell.tsx),
 * which is the reference for every product subdomain. Previously this passed no
 * `logo`, so the Header fell back to the nself.org CLI mark and sentry.nself.org
 * advertised itself as "ɳSELF CLI" — the wrong product. It also shipped a
 * version pill and a "Launch app" button, neither of which belongs on a product
 * SaaS surface.
 *
 * The shared shape across product subdomains:
 *   logo       -> <Logo product="<Name>" />   (product mark, not the CLI mark)
 *   logoHref   -> "/"                          (own site root, not nself.org)
 *   githubUrl  -> that product's own repo
 *   account    -> Log in + Sign up free        (SaaS CTAs)
 *
 * Named SiteHeader (not Header) to avoid collision with the @nself-web/ui
 * Header export.
 */
'use client'

import { Header as UIHeader, Logo } from '@nself-web/ui'
import type { HeaderNavItem } from '@nself-web/ui'

const NAV_ITEMS: HeaderNavItem[] = [
  { label: 'nself.org', href: 'https://nself.org', external: true },
  { label: 'Pricing', href: '/pricing' },
  { label: 'Demo', href: '/demo' },
  { label: 'Migrate', href: '/migrate' },
  { label: 'Self-host', href: '/self-host' },
  { label: 'Changelog', href: '/changelog' },
]

function AccountSlot() {
  return (
    <div className="flex items-center gap-2">
      <a
        href="/app/login"
        className="hidden sm:inline-flex items-center rounded-md px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-200 hover:text-sky-600 dark:hover:text-sky-700 dark:text-sky-400 transition-colors"
      >
        Log in
      </a>
      <a
        href="/app/signup"
        className="inline-flex items-center rounded-md bg-sky-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-sky-500 transition-colors"
      >
        Sign up free
      </a>
    </div>
  )
}

export function SiteHeader() {
  return (
    <UIHeader
      logo={<Logo className="h-8 w-auto" product="Sentry" />}
      logoHref="/"
      navItems={NAV_ITEMS}
      githubUrl="https://github.com/nself-org/nsentry"
      githubStars={null}
      chatUrl={null}
      accountButton={<AccountSlot />}
    />
  )
}
